// Atoms
import EImage from 'components/atoms/media/image/index.vue';
import ELogo from 'components/atoms/media/Logo.vue';
import EVector from 'components/atoms/media/Vector.vue';
import ECopyright from 'components/atoms/typography/Copyright.vue';
import EKbd from 'components/atoms/Kbd.vue';
import ELinkBase from 'components/atoms/interactive-elements/LinkBase.vue';
import EText from 'components/atoms/typography/Text.vue';

// Molecules
import EButton from 'components/molecules/Button.vue';
import EAppearance from 'components/molecules/Appearance.vue';
import EHeading from 'components/molecules/Heading.vue';
import EAlert from 'components/molecules/Alert.vue';
import EField from 'components/molecules/Field.vue';
import EInput from 'components/molecules/Input.vue';
import ECheckbox from 'components/molecules/Checkbox.vue';
import ERadio from 'components/molecules/Radio.vue';
import EInputPin from 'components/molecules/InputPin.vue';
import ESelect from 'components/molecules/Select.vue';
import EInputNumber from 'components/molecules/InputNumber.vue';

// Accordion
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionContent,
  AccordionTrigger
} from 'components/Accordion';

// Card
import { Card, CardHeader, CardContent, CardFooter } from 'components/Card';

// Icon
import { Icon } from 'components/Icon';

import { Menu, MenuItem } from 'components/Menu';

import EQrCode from 'components/QrCode/QrCode.vue';
import EEditor from 'components/Editor/Editor.vue';
import type { EditorProps } from 'components/Editor/EditorProps';

// Organisms
import EFormLayout from 'components/organisms/FormLayout.vue';
import ELayout from 'components/Layout/Layout.vue';
import EToast from 'components/organisms/Toast.vue';
import EDebug from 'components/Debug/Debug.vue';
import EGridLayout from 'components/organisms/GridLayout.vue';

// Styles
import 'tailwindcss/index.css';
import '../assets/css/main.css';
import '../assets/css/elements.css';

import { createPinia } from 'pinia';
import { defineEConnectElement } from './defineEConnectElementsApi';
import { defaultElementOptions } from './eConnectElementOptions';

export type { ElementDefinition, ElementDoc, PropDoc, StoryDoc, StyleDoc } from 'shared';
export { elementDefinitions, elementDefinitionsBySlug } from './elements-metadata';

/**
 * Shared Pinia instance used by the EConnect components.
 */
const pinia = createPinia();

/* -------------------------------------------------------------------------- */
/*  Component definitions                                                      */
/* -------------------------------------------------------------------------- */

// Atoms
const iconComponent = defineEConnectElement(Icon, defaultElementOptions);
const eImageComponent = defineEConnectElement(EImage, defaultElementOptions);
const eLogoComponent = defineEConnectElement(ELogo, defaultElementOptions);
const eVectorComponent = defineEConnectElement(EVector, defaultElementOptions);
const eCopyrightComponent = defineEConnectElement(ECopyright, defaultElementOptions);
const eKbdComponent = defineEConnectElement(EKbd, defaultElementOptions);
const eLinkBaseComponent = defineEConnectElement(ELinkBase, defaultElementOptions);
const eTextComponent = defineEConnectElement(EText, defaultElementOptions);

// Molecules
const eButtonComponent = defineEConnectElement(EButton, {
  ...defaultElementOptions,
  formSubmitter: true
});

const eAppearanceComponent = defineEConnectElement(EAppearance, defaultElementOptions);
const eHeadingComponent = defineEConnectElement(EHeading, defaultElementOptions);
const eFieldComponent = defineEConnectElement(EField, defaultElementOptions);
const eAlertComponent = defineEConnectElement(EAlert, defaultElementOptions);
const eToastComponent = defineEConnectElement(EToast, defaultElementOptions);

const eInputComponent = defineEConnectElement(EInput, {
  ...defaultElementOptions,
  formAssociated: true
});
const eCheckboxComponent = defineEConnectElement(ECheckbox, {
  ...defaultElementOptions,
  formAssociated: true
});
const eRadioComponent = defineEConnectElement(ERadio, {
  ...defaultElementOptions,
  formAssociated: true
});
const eInputPinComponent = defineEConnectElement(EInputPin, {
  ...defaultElementOptions,
  formAssociated: true
});
const eSelectComponent = defineEConnectElement(ESelect, {
  ...defaultElementOptions,
  formAssociated: true
});
const eInputNumberComponent = defineEConnectElement(EInputNumber, {
  ...defaultElementOptions,
  formAssociated: true
});
const menuItemComponent = defineEConnectElement(MenuItem, defaultElementOptions);

// Organisms
const eFormLayoutComponent = defineEConnectElement(EFormLayout, defaultElementOptions);
const eLayoutComponent = defineEConnectElement(ELayout, defaultElementOptions);
const menuComponent = defineEConnectElement(Menu, defaultElementOptions);
const eDebugComponent = defineEConnectElement(EDebug, defaultElementOptions);
const eGridLayoutComponent = defineEConnectElement(EGridLayout, defaultElementOptions);

// Accordion
const accordionComponent = defineEConnectElement(Accordion, defaultElementOptions);
const accordionItemComponent = defineEConnectElement(AccordionItem, defaultElementOptions);
const accordionHeaderComponent = defineEConnectElement(AccordionHeader, defaultElementOptions);
const accordionContentComponent = defineEConnectElement(AccordionContent, defaultElementOptions);
const accordionTriggerComponent = defineEConnectElement(AccordionTrigger, defaultElementOptions);

// Card
const cardComponent = defineEConnectElement(Card, defaultElementOptions);
const cardHeaderComponent = defineEConnectElement(CardHeader, defaultElementOptions);
const cardContentComponent = defineEConnectElement(CardContent, defaultElementOptions);
const cardFooterComponent = defineEConnectElement(CardFooter, defaultElementOptions);

// QR Code
const eQrCodeComponent = defineEConnectElement(EQrCode, defaultElementOptions);

// Editor
const eEditorComponent = defineEConnectElement(EEditor, {
  ...defaultElementOptions,
  customElementOptions: {
    shadowRoot: true
  }
});

/**
 * Public type for the `<e-editor>` custom element instance.
 *
 * Includes both the underlying element instance and its props.
 */
export type EEditorElement = InstanceType<typeof eEditorComponent> & {
  [K in keyof EditorProps]?: EditorProps[K];
};

/* -------------------------------------------------------------------------- */
/*  Public exports                                                            */
/* -------------------------------------------------------------------------- */

export {
  // Atoms
  eImageComponent,
  eLogoComponent,
  eVectorComponent,
  eCopyrightComponent,
  eKbdComponent,
  eLinkBaseComponent,
  eTextComponent,

  // Molecules
  eButtonComponent,
  eAppearanceComponent,
  eHeadingComponent,
  eAlertComponent,
  eFieldComponent,
  eToastComponent,
  eInputComponent,
  eCheckboxComponent,
  eRadioComponent,
  eInputPinComponent,
  eSelectComponent,
  eInputNumberComponent,

  // Organisms
  eFormLayoutComponent,
  eLayoutComponent,
  eDebugComponent,
  eGridLayoutComponent,

  // Accordion
  accordionComponent,
  accordionItemComponent,
  accordionHeaderComponent,
  accordionContentComponent,
  accordionTriggerComponent,

  // Card
  cardComponent,
  cardHeaderComponent,
  cardContentComponent,
  cardFooterComponent,

  // Menu Elements
  menuComponent,
  menuItemComponent,

  // Icon Elements
  iconComponent,

  // Other elements
  eQrCodeComponent,
  eEditorComponent,

  // Composables / utilities
  pinia
};

export { useToast } from 'composables/useToast';
export { useStateStore } from 'composables/useStateStore';
export { defineEConnectElement } from './defineEConnectElementsApi';
export { defaultElementOptions } from './eConnectElementOptions';
export { setupTurboLifecycle } from './turboLifecycle';
/**
 * Clears all component state from the Pinia store.
 *
 * **When to use**:
 * - SPA frameworks that reuse the same DOM/JavaScript context between pages
 * - Prevents stale component references and memory leaks during navigation
 *
 * **When NOT needed**:
 * - Full page reloads (browser clears everything automatically)
 * - Server-rendered pages without client-side navigation
 * - Individual component cleanup (handled automatically by disconnectedCallback)
 *
 * @example
 * // Vue Router
 * router.beforeEach(() => {
 *   clearEConnectState();
 * });
 *
 * @example
 * // React Router
 * function App() {
 *   useEffect(() => {
 *     return () => clearEConnectState();
 *   }, [location.pathname]);
 * }
 */
export function clearEConnectState(): void {
  try {
    interface StateStore {
      components?: Map<string, unknown>;
      tree?: Map<string, unknown>;
      globalShortcuts?: unknown[];
      state?: { value: Record<string, unknown> };
    }
    const stateStore = pinia?.state?.value?.state as StateStore | undefined;
    if (stateStore) {
      // Clear all component registrations
      stateStore.components?.clear?.();
      // Clear the component tree
      stateStore.tree?.clear?.();
      // Reset any other state as needed
      if (stateStore.globalShortcuts) {
        stateStore.globalShortcuts = [];
      }
    }
  } catch (e) {
    console.warn('Failed to clear EConnect state:', e);
  }
}

/* -------------------------------------------------------------------------- */
/*  Custom element registration                                               */
/* -------------------------------------------------------------------------- */

/**
 * Defines a custom element if it has not been defined yet.
 *
 * @param tag - Custom element tag name.
 * @param ctor - Constructor for the custom element.
 */
const define = (tag: string, ctor: CustomElementConstructor): void => {
  if (!customElements.get(tag)) {
    customElements.define(tag, ctor);
  }
};

/**
 * Registers all EConnect custom elements with the browser's
 * {@link customElements} registry.
 *
 * Safe to call multiple times; existing definitions are left untouched.
 */
export function register(): void {
  define('e-icon', iconComponent);
  define('e-image', eImageComponent);
  define('e-logo', eLogoComponent);
  define('e-vector', eVectorComponent);
  define('e-copyright', eCopyrightComponent);
  define('e-kbd', eKbdComponent);
  define('e-link-base', eLinkBaseComponent);
  define('e-text', eTextComponent);

  define('e-input', eInputComponent);
  define('e-checkbox', eCheckboxComponent);
  define('e-radio', eRadioComponent);
  define('e-input-pin', eInputPinComponent);
  define('e-select', eSelectComponent);
  define('e-input-number', eInputNumberComponent);
  define('e-button', eButtonComponent);
  define('e-appearance', eAppearanceComponent);
  define('e-heading', eHeadingComponent);
  define('e-alert', eAlertComponent);
  define('e-field', eFieldComponent);
  define('e-toast', eToastComponent);
  define('e-menu-item', menuItemComponent);

  define('e-accordion', accordionComponent);
  define('e-accordion-item', accordionItemComponent);
  define('e-accordion-header', accordionHeaderComponent);
  define('e-accordion-content', accordionContentComponent);
  define('e-accordion-trigger', accordionTriggerComponent);

  define('e-card', cardComponent);
  define('e-card-header', cardHeaderComponent);
  define('e-card-content', cardContentComponent);
  define('e-card-footer', cardFooterComponent);

  define('e-qr-code', eQrCodeComponent);

  define('e-form-layout', eFormLayoutComponent);
  define('e-layout', eLayoutComponent);
  define('e-menu', menuComponent);
  define('e-debug', eDebugComponent);
  define('e-grid-layout', eGridLayoutComponent);

  define('e-editor', eEditorComponent);
}

/* -------------------------------------------------------------------------- */
/*  Vue global component typings                                              */
/* -------------------------------------------------------------------------- */

declare module 'vue' {
  export interface GlobalComponents {
    // Atoms
    Icon: typeof iconComponent;
    EImage: typeof eImageComponent;
    ELogo: typeof eLogoComponent;
    EVector: typeof eVectorComponent;
    ECopyright: typeof eCopyrightComponent;
    EKbd: typeof eKbdComponent;
    ELinkBase: typeof eLinkBaseComponent;
    EText: typeof eTextComponent;

    // Molecules
    EInput: typeof eInputComponent;
    ECheckbox: typeof eCheckboxComponent;
    ERadio: typeof eRadioComponent;
    EInputPin: typeof eInputPinComponent;
    ESelect: typeof eSelectComponent;
    EInputNumber: typeof eInputNumberComponent;
    EButton: typeof eButtonComponent;
    EAppearance: typeof eAppearanceComponent;
    EHeading: typeof eHeadingComponent;
    EAlert: typeof eAlertComponent;
    EField: typeof eFieldComponent;
    EToast: typeof eToastComponent;
    EMenuItem: typeof menuItemComponent;

    // Organisms
    EFormLayout: typeof eFormLayoutComponent;
    ELayout: typeof eLayoutComponent;
    EMenu: typeof menuComponent;
    EDebug: typeof eDebugComponent;
    EGridLayout: typeof eGridLayoutComponent;

    // Accordion
    EAccordion: typeof accordionComponent;
    EAccordionItem: typeof accordionItemComponent;
    EAccordionHeader: typeof accordionHeaderComponent;
    EAccordionContent: typeof accordionContentComponent;
    EAccordionTrigger: typeof accordionTriggerComponent;

    // Card
    ECard: typeof cardComponent;
    ECardHeader: typeof cardHeaderComponent;
    ECardContent: typeof cardContentComponent;
    ECardFooter: typeof cardFooterComponent;

    // Other elements
    EQrCode: typeof eQrCodeComponent;
    EEditor: typeof eEditorComponent;
  }
}
